import React, { useState, useEffect, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { fetchReports } from "../../store/researchReportSlice";
import { fetchIndustries } from "../../store/industrySlice";
import FiltersBar from "../../common/FiltersBar";
import SearchBar from "../../common/SearchBar";
import "../../styles/syndicated-reports.scss";
import Categories from "./Categories";
import Pagination from "../../common/Pagination";
import ReportsHero from "../../common/ReportsHero";
import HomeHeroVisual from "./HomeHeroVisual";
import LatestReportsBar from "../../common/LatestReportsBar";
import ReportCard from "./ReportCard";
import Workflow from "../HowItWorks/Workflow";
import { IoCloseOutline } from "react-icons/io5";
import { FaFileAlt, FaBuilding, FaGlobeAmericas } from "react-icons/fa";
import { SeoManager } from "../../common/SeoManager";
import { useAuth } from "../../context/AuthContext";

const AnimatedNumber = ({ valueStr, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  const numericPart = String(valueStr).replace(/[^0-9]/g, "");
  const endValue = parseInt(numericPart, 10) || 0;
  const suffix = String(valueStr).replace(/[0-9,]/g, "");

  useEffect(() => {
    if (endValue === 0) {
      setCount(0);
      return;
    }

    let startTime = null;
    let animationFrame;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const percentage = Math.min(progress / duration, 1);

      const easeProgress =
        percentage === 1 ? 1 : 1 - Math.pow(2, -10 * percentage);

      setCount(Math.floor(endValue * easeProgress));

      if (progress < duration) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(endValue);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [endValue, duration]);

  return (
    <>
      {count.toLocaleString()}
      {suffix}
    </>
  );
};

const slugify = (text) => {
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Researchwire Market",
  url: "https://rwiremarket.com",
  logo: "https://rwiremarket.com/Rwirelogo.png",
  description:
    "Premium syndicated market research reports and strategic intelligence for global industries.",
  address: {
    "@type": "PostalAddress",
    addressCountry: "India",
  },
  sameAs: [
    "https://www.linkedin.com/company/researchwire-market-intelligence/",
    "https://twitter.com/researchwire",
  ],
};

const mockReports = [
  {
    id: 1,
    title: "Global AI Market Report 2024",
    category: "Technology",
    industry: "Artificial Intelligence",
    subCategory: "AI Forecast",
    RegionsCovered: "North America, Europe",
    CountriesCovered: "USA, UK, Germany",
    ProductCode: "AI-2024",
    price: 1999,
    currency: "USD",
    description:
      "Comprehensive analysis of AI market trends, forecasts, and key players.",
    thumbnail: "https://via.placeholder.com/150",
  },
  {
    id: 2,
    title: "EV Battery Market Insights",
    category: "Energy",
    industry: "Electric Vehicles",
    subCategory: "Battery Technology",
    RegionsCovered: "Asia, Europe",
    CountriesCovered: "China, India, Germany",
    ProductCode: "EVB-2024",
    price: 1499,
    currency: "USD",
    description:
      "In‑depth report on EV battery technologies, supply chain, and market outlook.",
    thumbnail: "https://via.placeholder.com/150",
  },
];

const SyndicatedReportsPage = () => {
  const { endUser } = useAuth() || {};
  const dispatch = useDispatch();
  const location = useLocation();
  const { industrySlug } = useParams();
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  const { reports, loading, error } = useSelector(
    (state) => state.researchReports,
  );
  const { hierarchy: fetchedIndustries } = useSelector(
    (state) => state.industries,
  ) || { hierarchy: {} };

  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);

  const [pageSize, setPageSize] = useState(30);
  const baseReports = useMemo(() => {
    const sourceReports = reports.length > 0 ? reports : mockReports;
    const published = sourceReports.filter(
      (r) => !r.status || r.status.toLowerCase() === "published",
    );
    // Sort so newest reports (by createdAt or id) appear at the top
    return published.sort((a, b) => {
      if (a.createdAt && b.createdAt) {
        return new Date(b.createdAt) - new Date(a.createdAt);
      }
      return (b.id || 0) - (a.id || 0);
    });
  }, [reports]);

  // Base Search Filter Logic (used by all other filters)
  const searchFilteredReports = useMemo(() => {
    let result = [...baseReports];
    if (searchQuery) {
      let rawQuery = searchQuery.toLowerCase().trim();
      let expandedQuery = rawQuery;

      const synonymMappings = [
        { pattern: /\bai\b/g, addition: "artificial intelligence" },
        { pattern: /\bml\b/g, addition: "machine learning" },
        { pattern: /\biot\b/g, addition: "internet of things" },
        { pattern: /\bev\b/g, addition: "electric vehicle vehicles" },
        { pattern: /\bar\b/g, addition: "augmented reality" },
        { pattern: /\bvr\b/g, addition: "virtual reality" },
        { pattern: /artificial intelligence/g, addition: "ai" },
        { pattern: /machine learning/g, addition: "ml" },
        { pattern: /internet of things/g, addition: "iot" },
        { pattern: /electric vehicles?/g, addition: "ev" },
        { pattern: /augmented reality/g, addition: "ar" },
        { pattern: /virtual reality/g, addition: "vr" },
      ];

      let additions = "";
      synonymMappings.forEach((mapping) => {
        if (mapping.pattern.test(expandedQuery)) {
          additions += " " + mapping.addition;
        }
      });
      expandedQuery += additions;

      const allTerms = expandedQuery.trim().split(/\s+/);
      const stopWords = [
        "market",
        "industry",
        "reports",
        "report",
        "analysis",
        "forecast",
        "size",
        "trends",
        "growth",
      ];
      const meaningfulTerms = allTerms.filter((t) => !stopWords.includes(t));
      const termsToMatch =
        meaningfulTerms.length > 0 ? meaningfulTerms : allTerms;

      const scoredReports = result.map((r) => {
        const titlePart = (r.title || "").toLowerCase();
        const catPart = (r.category || r.industry || r.Industry || "").toLowerCase();
        const subPart = (r.subCategory || r.sub_category || r.sub_industry || "").toLowerCase();
        const searchContentCombined = `${titlePart} ${catPart} ${subPart}`;

        let matchedCount = 0;
        let score = 0;

        if (titlePart.includes(rawQuery)) {
          score += 200;
        }

        termsToMatch.forEach((term, index) => {
          const weight = (termsToMatch.length - index) * 10;
          let matched = false;

          if (term.length <= 3) {
            const safeTerm = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
            const regex = new RegExp(`\\b${safeTerm}\\b`, "i");
            if (regex.test(searchContentCombined)) {
              matched = true;
            }
          } else {
            if (searchContentCombined.includes(term)) {
              matched = true;
            }
          }

          if (matched) {
            matchedCount++;
            score += weight;
            if (titlePart.includes(term)) {
              score += 20;
            }
          }
        });

        return { report: r, score, matchedCount };
      });

      const matchingReports = scoredReports.filter((item) => item.score > 0);

      if (matchingReports.length > 0) {
        const maxScore = Math.max(...matchingReports.map((i) => i.score));
        const maxMatchedCount = Math.max(...matchingReports.map((i) => i.matchedCount));

        let scoreThreshold = 1;
        if (termsToMatch.length >= 2) {
          if (maxMatchedCount > 1 || maxScore >= 100) {
            scoreThreshold = Math.max(30, maxScore * 0.4);
          }
        }

        const filtered = matchingReports
          .filter((item) => item.score >= scoreThreshold)
          .sort((a, b) => b.score - a.score)
          .map((item) => item.report);

        result = filtered;
      } else {
        result = [];
      }
    }
    return result;
  }, [baseReports, searchQuery]);

  // Categories Sidebar Reports (depends on search, region, country)
  const sidebarReports = useMemo(() => {
    let result = searchFilteredReports;
    if (selectedRegions.length > 0) {
      result = result.filter((r) =>
        selectedRegions.some(
          (sr) =>
            r.RegionsCovered?.toLowerCase().includes(sr.toLowerCase()) ||
            r.region?.toLowerCase().includes(sr.toLowerCase()),
        ),
      );
    }
    if (selectedCountries.length > 0) {
      result = result.filter((r) =>
        selectedCountries.some(
          (sc) =>
            r.CountriesCovered?.toLowerCase().includes(sc.toLowerCase()) ||
            r.country?.toLowerCase().includes(sc.toLowerCase()),
        ),
      );
    }
    return result;
  }, [searchFilteredReports, selectedRegions, selectedCountries]);

  // Final Filtered Reports (what is actually shown on the page)
  const filteredReports = useMemo(() => {
    let result = sidebarReports || [];

    if (selectedCategory) {
      const cat = selectedCategory.toLowerCase().trim();
      result = result.filter(
        (r) =>
          (r.category && r.category.toLowerCase().includes(cat)) ||
          (r.industry && r.industry.toLowerCase().includes(cat)) ||
          (r.Industry && r.Industry.toLowerCase().includes(cat)),
      );
    }

    if (selectedSubCategory) {
      const sub = selectedSubCategory.toLowerCase().trim();
      result = result.filter(
        (r) =>
          (r.subCategory && r.subCategory.toLowerCase().includes(sub)) ||
          (r.sub_category && r.sub_category.toLowerCase().includes(sub)) ||
          (r.sub_industry && r.sub_industry.toLowerCase().includes(sub)),
      );
    }

    return result;
  }, [sidebarReports, selectedCategory, selectedSubCategory]);

  // Pagination Logic
  const isFiltered = !!(
    searchQuery ||
    selectedCategory ||
    selectedSubCategory ||
    selectedRegions.length > 0 ||
    selectedCountries.length > 0
  );
  const effectivePageSize = pageSize;

  const totalPages = Math.max(
    1,
    Math.ceil(filteredReports.length / effectivePageSize),
  );
  const startIndex = (currentPage - 1) * effectivePageSize;
  const currentItems = filteredReports.slice(
    startIndex,
    startIndex + effectivePageSize,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    searchQuery,
    selectedCategory,
    selectedSubCategory,
    selectedRegions,
    selectedCountries,
  ]);
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  useEffect(() => {
    // Initial fetch
    dispatch(fetchReports());
    dispatch(fetchIndustries());
  }, [dispatch]);

  useEffect(() => {
    const isIndustryRoute =
      industrySlug && industrySlug.startsWith("reports-industry-");
    const activeSlug = isIndustryRoute
      ? industrySlug.replace("reports-industry-", "")
      : null;

    const params = new URLSearchParams(location.search);
    const industryParam = params.get("industry");
    const subcategoryParam = params.get("subcategory");
    const searchParam = params.get("search");
    const initialSearchState = location.state?.initialSearch;
    const searchToUse = searchParam || initialSearchState;

    if (activeSlug) {
      // Find a report that has an industry matching this slug
      const matchedReport = baseReports.find((r) => {
        const cat = r.industry || r.category || r.Industry;
        if (cat) {
          const industries = [cat.trim()];
          return industries.some((ind) => slugify(ind) === activeSlug);
        }
        return false;
      });

      if (matchedReport) {
        const cat =
          matchedReport.industry ||
          matchedReport.category ||
          matchedReport.Industry;
        const originalIndustry = [cat.trim()].find(
          (ind) => slugify(ind) === activeSlug,
        );
        if (originalIndustry) {
          setSelectedCategory(originalIndustry.trim());
        }
      } else {
        // Fallback: capitalize words
        const fallback = activeSlug
          .split("-")
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        setSelectedCategory(fallback);
      }
      setSelectedSubCategory(null);
      setSearchQuery("");
      if (resultsRef.current) {
        const yOffset = -120;
        const rect = resultsRef.current.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY + yOffset,
          behavior: "smooth",
        });
      }
    } else if (searchToUse) {
      setSearchQuery(searchToUse);

      // If searchToUse matches an industry category directly, set selectedCategory as well
      if (baseReports && baseReports.length > 0) {
        const queryLower = searchToUse.toLowerCase().trim();
        const matchedCategoryReport = baseReports.find((r) => {
          const cat = (r.category || r.industry || r.Industry || "").toLowerCase().trim();
          return cat === queryLower || cat.includes(queryLower);
        });
        if (matchedCategoryReport) {
          const catName = (matchedCategoryReport.category || matchedCategoryReport.industry || matchedCategoryReport.Industry).trim();
          if (catName.toLowerCase() === queryLower) {
            setSelectedCategory(catName);
          }
        }
      }
      setSelectedSubCategory(null);
      if (resultsRef.current) {
        const yOffset = -120;
        const rect = resultsRef.current.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY + yOffset,
          behavior: "smooth",
        });
      }
    } else if (industryParam) {
      setSelectedCategory(industryParam);
      setSelectedSubCategory(null);
      setSearchQuery("");
      if (resultsRef.current) {
        const yOffset = -120;
        const rect = resultsRef.current.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY + yOffset,
          behavior: "smooth",
        });
      }
    } else if (subcategoryParam) {
      setSelectedSubCategory(subcategoryParam);
      setSearchQuery("");
      // Try to find parent category from reports if available
      if (baseReports && baseReports.length > 0) {
        const sub = subcategoryParam.toLowerCase().trim();
        const match = baseReports.find(
          (r) =>
            (r.subCategory && r.subCategory.toLowerCase().includes(sub)) ||
            (r.sub_category && r.sub_category.toLowerCase().includes(sub)) ||
            (r.sub_industry && r.sub_industry.toLowerCase().includes(sub)),
        );
        if (match) {
          const cat = match.industry || match.category || match.Industry;
          if (cat) {
            const mainCat = typeof cat === "string" ? cat.trim() : cat;
            setSelectedCategory(mainCat);
          }
        }
      }

      if (resultsRef.current) {
        const yOffset = -120;
        const rect = resultsRef.current.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY + yOffset,
          behavior: "smooth",
        });
      }
    } else {
      // Clear everything if no params
      setSearchQuery("");
      setSelectedCategory(null);
      setSelectedSubCategory(null);
    }
  }, [industrySlug, location.search, location.state, baseReports]); // Added baseReports as dependency to allow parent category matching once data loads

  // Smooth scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  const uniqueCategories = useMemo(() => {
    const fetchedKeys = Object.keys(fetchedIndustries || {});
    if (fetchedKeys.length > 0) {
      return fetchedKeys;
    }
    const cats = new Set();
    sidebarReports.forEach((r) => {
      [r.category, r.industry, r.Industry]
        .filter(Boolean)
        .forEach((c) => cats.add(c.replace(/\s+/g, " ").trim()));
    });
    return [...cats];
  }, [fetchedIndustries, sidebarReports]);

  const uniqueSubCategories = useMemo(() => {
    const subs = new Set();
    sidebarReports.forEach((r) => {
      [r.subCategory, r.sub_category, r.sub_industry]
        .filter(Boolean)
        .forEach((s) => subs.add(s.replace(/\s+/g, " ").trim()));
    });
    return [...subs];
  }, [sidebarReports]);

  // For Regions: depends on search, category, country
  const reportsForRegionFilter = useMemo(() => {
    let result = searchFilteredReports;
    if (selectedCategory) {
      const cat = selectedCategory.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.category?.toLowerCase().includes(cat) ||
          r.industry?.toLowerCase().includes(cat) ||
          r.Industry?.toLowerCase().includes(cat),
      );
    }
    if (selectedSubCategory) {
      const sub = selectedSubCategory.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.subCategory?.toLowerCase().includes(sub) ||
          r.sub_category?.toLowerCase().includes(sub) ||
          r.sub_industry?.toLowerCase().includes(sub),
      );
    }
    if (selectedCountries.length > 0) {
      result = result.filter((r) =>
        selectedCountries.some(
          (sc) =>
            r.CountriesCovered?.toLowerCase().includes(sc.toLowerCase()) ||
            r.country?.toLowerCase().includes(sc.toLowerCase()),
        ),
      );
    }
    return result;
  }, [
    searchFilteredReports,
    selectedCategory,
    selectedSubCategory,
    selectedCountries,
  ]);

  const uniqueRegions = useMemo(
    () =>
      [
        ...new Set(
          reportsForRegionFilter.map((r) => r.region || r.RegionsCovered),
        ),
      ].filter(Boolean),
    [reportsForRegionFilter],
  );

  // For Countries: depends on search, category, region
  const reportsForCountryFilter = useMemo(() => {
    let result = searchFilteredReports;
    if (selectedCategory) {
      const cat = selectedCategory.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.category?.toLowerCase().includes(cat) ||
          r.industry?.toLowerCase().includes(cat) ||
          r.Industry?.toLowerCase().includes(cat),
      );
    }
    if (selectedSubCategory) {
      const sub = selectedSubCategory.toLowerCase().trim();
      result = result.filter(
        (r) =>
          r.subCategory?.toLowerCase().includes(sub) ||
          r.sub_category?.toLowerCase().includes(sub) ||
          r.sub_industry?.toLowerCase().includes(sub),
      );
    }
    if (selectedRegions.length > 0) {
      result = result.filter((r) =>
        selectedRegions.some(
          (sr) =>
            r.RegionsCovered?.toLowerCase().includes(sr.toLowerCase()) ||
            r.region?.toLowerCase().includes(sr.toLowerCase()),
        ),
      );
    }
    return result;
  }, [
    searchFilteredReports,
    selectedCategory,
    selectedSubCategory,
    selectedRegions,
  ]);

  const uniqueCountries = useMemo(() => {
    const countries = new Set();
    reportsForCountryFilter.forEach((r) => {
      if (r.country) countries.add(r.country);
      if (r.CountriesCovered) {
        // Handle potentially comma-separated countries
        r.CountriesCovered.split(",").forEach((c) => countries.add(c.trim()));
      }
    });
    return [...countries].filter(Boolean);
  }, [reportsForCountryFilter]);

  const stats = useMemo(
    () => [
      {
        label: "Reports",
        value:
          baseReports.length > 0
            ? `${baseReports.length.toLocaleString()}`
            : "0",
        icon: FaFileAlt,
      },
      {
        label: "Industries",
        value: uniqueCategories.length > 0 ? `${uniqueCategories.length}` : "0",
        icon: FaBuilding,
      },
      { label: "Countries Covered", value: "50+", icon: FaGlobeAmericas },
    ],
    [baseReports.length, uniqueCategories.length],
  );

  const handleClearAllFilters = () => {
    setSelectedRegions([]);
    setSelectedCountries([]);
    setSearchQuery("");
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    navigate("/reports");
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    // Reset category/subcategory when searching to allow global discovery
    setSelectedCategory(null);
    setSelectedSubCategory(null);

    if (resultsRef.current) {
      const yOffset = -90; // Adjust for header heights
      const y =
        resultsRef.current.getBoundingClientRect().top +
        window.scrollY +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Removed global loading spinner to allow progressive rendering (SEO & CLS improvement)

  return (
    <main className="bg-white min-vh-100 herotitle">
      <SeoManager
        title="Researchwire Market Intelligence Platform"
        description="Explore thousands of premium market research reports with strategic intelligence, market forecasts, and growth analysis across 15+ industries."
        ogTitle="Explore Global Market Research Reports | Researchwire Market Intelligence"
        ogDescription="Get access to premium syndicated market research reports and strategic industry intelligence for informed business decision-making."
        ogUrl="https://www.rwmarketintelligence.com/"
        ogImage="https://www.rwmarketintelligence.com/Rwirelogo.png"
        canonicalUrl="https://www.rwmarketintelligence.com/"
        schema={organizationSchema}
      />
      <ReportsHero
        title="Explore Our Global Market Research Repository"
        description={`Find industry insights, market forecasts, competitive analysis, and growth opportunities across ${uniqueCategories.length > 0 ? uniqueCategories.length : "15+"} sectors.`}
        customImageSection={<HomeHeroVisual reports={baseReports} />}
        fullWidthVisual={true}
        centered={true}
      >
        <style>
          {`
                    @media (min-width: 768px) {
                        .responsive-sidebar {
                            position: sticky;
                            top: 80px;
                            height: fit-content;
                            z-index: 5;
                        }
                    }
                    `}
        </style>
        <div className="mt-4 w-100">
          <div
            className="d-flex flex-nowrap justify-content-start justify-content-md-center overflow-x-auto gap-1 gap-lg-4 mb-4 pb-2 w-100"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            <div
              className="bg-white rounded-3 shadow-sm px-4 py-2 d-flex align-items-center gap-4 border"
              style={{ minWidth: "max-content" }}
            >
              {stats.map((stat, idx) => (
                <React.Fragment key={idx}>
                  <div className="d-flex align-items-center gap-2 flex-shrink-0">
                    <div
                      className="d-flex align-items-center justify-content-center p-1 rounded"
                      style={{ color: "#053b84" }}
                    >
                      {typeof stat.icon === "string" ? (
                        <img
                          src={stat.icon}
                          alt={stat.label}
                          style={{
                            width: "20px",
                            height: "20px",
                            objectFit: "contain",
                            filter:
                              "invert(36%) sepia(87%) saturate(2256%) hue-rotate(200deg) brightness(98%) contrast(106%)",
                          }}
                        />
                      ) : (
                        <stat.icon size={20} style={{ color: "#053b84" }} />
                      )}
                    </div>
                    <div className="d-flex align-items-center gap-1 text-nowrap">
                      <span className="fw-bold text-dark hero-stat-value">
                        <AnimatedNumber valueStr={stat.value} />
                      </span>
                      <span className="text-secondary hero-stat-label">
                        {stat.label}
                      </span>
                    </div>
                  </div>
                  {idx < stats.length - 1 && (
                    <div
                      className="d-none d-lg-block border-end border-light ms-2"
                      style={{ height: "24px" }}
                    ></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <SearchBar
            value={searchQuery}
            onChange={(val) => setSearchQuery(val)}
            onSearch={handleSearch}
            placeholder="Search market reports, industries, or keywords..."
            debounce={300}
            showButton={true}
            buttonText="Search"
            enableSuggestions={true}
          />
          <div className="mt-4 w-100 d-flex flex-wrap align-items-center justify-content-center gap-2 pb-2">
            <span className="populartext text-secondary small fw-medium flex-shrink-0">
              Popular Searches:
            </span>
            {[
              "Artificial Intelligence",
              "Electric Vehicles",
              "Sustainable Packaging",
              "Green Hydrogen",
              "Medical Devices",
              "Semiconductor Materials",
              "Carbon Capture",
              "Specialty Chemicals",
              "Marine Automation",
              "Consumer & Lifestyle Services",
              "Data Center",
            ].map((tag) => (
              <button
                key={tag}
                className={`btn popular-search-pill text-nowrap flex-shrink-0 ${searchQuery === tag ? "active" : ""}`}
                onClick={() => handleSearch(tag)}
                aria-label={`Search for ${tag}`}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      </ReportsHero>

      <LatestReportsBar />

      <div className="category-container">
        <div className="container-fluid bg-white">
          <div className="row p-lg-4 p-3">
            <div className="col-lg-3 col-md-4 mb-4 responsive-sidebar">
              <Categories
                availableCategories={uniqueCategories}
                availableSubCategories={uniqueSubCategories}
                selectedCategory={selectedCategory}
                selectedSubCategory={selectedSubCategory}
                baseReports={sidebarReports}
                onCategorySelect={(cat) => {
                  setSelectedCategory(cat);
                  setSelectedSubCategory(null);
                }}
                onSubCategorySelect={setSelectedSubCategory}
                isFiltered={isFiltered}
                onClearAll={handleClearAllFilters}
              />

              <div
                className="mt-4 p-3 rounded-4 text-center text-white position-relative overflow-hidden shadow-sm"
                style={{
                  background:
                    "linear-gradient(135deg, #053b84 0%, #1a237e 100%)",
                }}
              >
                <div
                  className="position-absolute top-0 end-0 opacity-25"
                  style={{ transform: "translate(20%, -20%) scale(2)" }}
                >
                  <svg
                    width="100"
                    height="100"
                    viewBox="0 0 100 100"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="50"
                      cy="50"
                      r="40"
                      stroke="white"
                      strokeWidth="2"
                      strokeDasharray="4 4"
                    />
                    <circle
                      cx="50"
                      cy="50"
                      r="20"
                      stroke="white"
                      strokeWidth="2"
                    />
                  </svg>
                </div>
                <div className="position-relative z-1">
                  <h5 className="fw-bold mb-1" style={{ fontSize: "1rem" }}>
                    Need a Customized Market Report?
                  </h5>
                  <button
                    className="mt-2 btn btn-light rounded-pill px-4 py-2 fw-bold w-100 shadow-sm transition"
                    onClick={() => {
                      const isUserLoggedIn = Boolean(
                        (endUser && (endUser.email || endUser.id)) ||
                        (() => {
                          try {
                            const u = JSON.parse(
                              localStorage.getItem("rw_end_user") || "null",
                            );
                            return u && (u.email || u.id);
                          } catch (e) {
                            return false;
                          }
                        })(),
                      );
                      const targetUrl = "/request-custom-research";
                      if (!isUserLoggedIn) {
                        navigate(
                          `/signin?redirect=${encodeURIComponent(targetUrl)}`,
                        );
                        return;
                      }
                      navigate(targetUrl);
                    }}
                    style={{ fontSize: "0.8rem", color: "#053b84" }}
                  >
                    Get a Custom Report
                  </button>
                </div>
              </div>
            </div>

            <div className="col-lg-9 col-md-8">
              <div
                className="py-2 mb-3 px-1 position-sticky bg-white"
                style={{ top: "80px", zIndex: 10 }}
                ref={resultsRef}
              >
                <div className="Filter-Results d-flex justify-content-between align-items-center gap-2 pb-3">
                  <div className="flex-shrink-0">
                    <h2 className="h6 fw-bold mb-0 text-dark text-nowrap">
                      Filter Results
                    </h2>
                    <div
                      className="text-muted fw-medium"
                      style={{ fontSize: "0.75rem" }}
                    >
                      Showing{" "}
                      <span className="text-dark fw-bold">
                        {currentItems.length}
                      </span>{" "}
                      of {filteredReports.length.toLocaleString()} reports
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    {isFiltered && (
                      <button
                        className="btn btn-link fw-bold p-0 text-decoration-none transition hover-text-primary"
                        onClick={handleClearAllFilters}
                        style={{ fontSize: "0.9rem", color: "#053b84" }}
                        aria-label="Clear all filters"
                      >
                        Clear Filters
                      </button>
                    )}
                    <FiltersBar
                      availableRegions={uniqueRegions}
                      availableCountries={uniqueCountries}
                      selectedRegions={selectedRegions}
                      onRegionsChange={setSelectedRegions}
                      selectedCountries={selectedCountries}
                      onCountriesChange={setSelectedCountries}
                    />
                  </div>
                </div>
                {loading && (
                  <div className="d-flex align-items-center justify-content-center py-2">
                    <div
                      className="spinner-border spinner-border-sm commonColor me-2"
                      role="status"
                    ></div>
                    <span className="small text-secondary">
                      Updating results...
                    </span>
                  </div>
                )}
                <hr className="my-0 opacity-25" />

                {/* Selected Chips Section */}
                {(selectedRegions.length > 0 ||
                  selectedCountries.length > 0) && (
                  <div className="pt-3">
                    <div className="d-flex flex-wrap gap-2 align-items-center">
                      {selectedRegions.map((region) => (
                        <div
                          key={region}
                          className="badge bg-light text-dark border d-flex align-items-center gap-1 py-1 px-3 rounded-pill"
                          style={{ fontWeight: "500", fontSize: "0.9rem" }}
                        >
                          {region}
                          <IoCloseOutline
                            className="cursor-pointer text-muted"
                            size={18}
                            onClick={() =>
                              setSelectedRegions(
                                selectedRegions.filter((r) => r !== region),
                              )
                            }
                            aria-label={`Remove ${region} filter`}
                          />
                        </div>
                      ))}

                      {selectedCountries.map((country) => (
                        <div
                          key={country}
                          className="badge bg-light text-dark border d-flex align-items-center gap-1 py-1 px-3 rounded-pill"
                          style={{ fontWeight: "500", fontSize: "0.9rem" }}
                        >
                          {country}
                          <IoCloseOutline
                            className="cursor-pointer text-muted"
                            size={18}
                            onClick={() =>
                              setSelectedCountries(
                                selectedCountries.filter((c) => c !== country),
                              )
                            }
                            aria-label={`Remove ${country} filter`}
                          />
                        </div>
                      ))}
                    </div>
                    <hr className="mt-3 mb-0 opacity-25" />
                  </div>
                )}
              </div>

              <div
                className="commonBorder p-2 rounded"
                style={{ minHeight: "600px" }}
              >
                {loading && currentItems.length === 0 ? (
                  <div className="text-center py-5">
                    <div
                      className="spinner-border commonColor"
                      role="status"
                    ></div>
                    <p className="mt-2 text-secondary">Loading reports...</p>
                  </div>
                ) : error ? (
                  <div className="text-center py-5 border rounded bg-light text-danger">
                    <h6 className="fw-bold">Error loading reports</h6>
                    <p className="mb-0">{error}</p>
                  </div>
                ) : currentItems.length === 0 ? (
                  <div className="text-center py-5 border rounded bg-light text-muted">
                    <h6 className="fw-bold">No reports</h6>
                    {isFiltered && (
                      <p className="mb-0">
                        Try adjusting your search or filters.
                      </p>
                    )}
                  </div>
                ) : (
                  <>
                    {currentItems.map((report, idx) => (
                      <ReportCard key={report.id || idx} report={report} />
                    ))}

                    <div className="mt-4 d-flex justify-content-center">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={(p) => setCurrentPage(p)}
                        pageSize={effectivePageSize}
                        totalItems={filteredReports.length}
                        itemName="Reports"
                        onPageSizeChange={(newSize) => {
                          setPageSize(newSize);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <Workflow />
      </div>
    </main>
  );
};

export default SyndicatedReportsPage;
